import { useState } from "react";
import { Button, CategorySelect, Input, KeywordsInput, LocationPicker, UbigeoSelect } from "@vimos/ui";
import type { MapCoordinates } from "@vimos/api-client";
import {
  getDirectoryCategoryLabel,
  normalizeSocialProfileUrl,
  type DirectorySocialNetwork,
} from "@vimos/api-client";
import { createManualStore, uploadDirectoryImage, type ManualStoreInput } from "./directoryApi";
import { BillingFields, emptyBillingTerms } from "./BillingFields";

interface Props {
  onCreated: () => void;
  onCancel: () => void;
}

const emptyForm = {
  name: "",
  category: "",
  description: "",
  department: "",
  province: "",
  district: "",
  address: "",
  phone: "",
  whatsapp: "",
  website_url: "",
};

export function ManualStoreForm({ onCreated, onCancel }: Props) {
  const [form, setForm] = useState(emptyForm);
  const [categoryId, setCategoryId] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [socialLinks, setSocialLinks] = useState<Record<DirectorySocialNetwork, string>>({
    tiktok: "",
    instagram: "",
    facebook: "",
  });
  const [location, setLocation] = useState<MapCoordinates | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [gallery, setGallery] = useState<string[]>([]);
  const [publish, setPublish] = useState(true);
  const [billingTerms, setBillingTerms] = useState(emptyBillingTerms);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof typeof emptyForm>(key: K, value: string) {
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  async function handleImage(
    event: React.ChangeEvent<HTMLInputElement>,
    target: "logo" | "cover" | "gallery",
  ) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    setError(null);
    try {
      if (target === "logo") {
        setLogoUrl(await uploadDirectoryImage(files[0]!));
      } else if (target === "cover") {
        setCoverUrl(await uploadDirectoryImage(files[0]!));
      } else {
        const uploaded: string[] = [];
        for (const file of files.slice(0, 8)) uploaded.push(await uploadDirectoryImage(file));
        setGallery((previous) => [...previous, ...uploaded].slice(0, 8));
      }
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "No se pudo subir la imagen.");
    } finally {
      event.target.value = "";
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    if (form.name.trim().length < 2) {
      setError("El nombre comercial es obligatorio.");
      return;
    }
    if (!categoryId) {
      setError("Selecciona una categoria del catalogo.");
      return;
    }
    if (
      [billingTerms.setupAmount, billingTerms.monthlyAmount, billingTerms.yearlyAmount].some(
        (amount) => amount !== "" && (!Number.isFinite(Number(amount)) || Number(amount) <= 0),
      )
    ) {
      setError("Los importes de alta y cobro recurrente deben ser mayores que cero.");
      return;
    }
    if (billingTerms.monthlyAmount && billingTerms.yearlyAmount) {
      setError("Elige una sola periodicidad recurrente: mensual o anual.");
      return;
    }
    if (billingTerms.isComped && !billingTerms.priceNote.trim()) {
      setError("Indica el motivo de la cortesía para dejarla registrada.");
      return;
    }
    const planAmount = billingTerms.planPeriod === "one_time"
      ? Number(billingTerms.setupAmount)
      : billingTerms.planPeriod === "yearly"
        ? Number(billingTerms.yearlyAmount)
        : Number(billingTerms.monthlyAmount);
    if (!billingTerms.isComped && billingTerms.planId && (!Number.isFinite(planAmount) || planAmount <= 0)) {
      setError("El plan seleccionado requiere un monto acordado mayor que cero.");
      return;
    }
    if (
      billingTerms.planId && !billingTerms.isComped && Number.isFinite(planAmount) && planAmount !== Number(billingTerms.planPrice) &&
      !billingTerms.priceNote.trim()
    ) {
      setError("Registra el motivo del precio especial para dejar trazabilidad.");
      return;
    }
    const normalizedSocials: Record<string, string> = {};
    for (const network of ["tiktok", "instagram", "facebook"] as const) {
      const raw = socialLinks[network].trim();
      if (!raw) continue;
      const normalized = normalizeSocialProfileUrl(network, raw);
      if (!normalized) {
        setError(`El enlace de ${network} debe ser un perfil válido de esa red social.`);
        return;
      }
      normalizedSocials[network] = normalized;
    }
    setIsSaving(true);
    try {
      const input: ManualStoreInput = {
        name: form.name,
        category: getDirectoryCategoryLabel(categoryId),
        category_id: categoryId,
        keywords,
        city: form.district || form.province || form.department,
        description: form.description,
        department: form.department,
        province: form.province,
        district: form.district,
        address: form.address,
        phone: form.phone,
        whatsapp: form.whatsapp,
        website_url: form.website_url,
        social_links: normalizedSocials,
        billing_plan_id: billingTerms.planId || null,
        billing_plan_name: billingTerms.planName || null,
        billing_plan_price: billingTerms.planPrice ? Number(billingTerms.planPrice) : null,
        billing_plan_period: billingTerms.planPeriod || null,
        billing_plan_snapshot: billingTerms.planSnapshot,
        billing_is_comped: billingTerms.isComped,
        billing_price_note: billingTerms.priceNote.trim() || null,
        google_maps_url: null,
        latitude: location?.latitude ?? null,
        longitude: location?.longitude ?? null,
        logo_url: logoUrl,
        cover_image_url: coverUrl,
        gallery,
        billing_setup_amount: Number(billingTerms.setupAmount) || 0,
        billing_setup_due_date: billingTerms.setupAmount ? billingTerms.setupDueDate : null,
        billing_monthly_amount: Number(billingTerms.monthlyAmount) || 0,
        billing_yearly_amount: Number(billingTerms.yearlyAmount) || 0,
        billing_start_date: billingTerms.monthlyAmount || billingTerms.yearlyAmount || billingTerms.isComped && (billingTerms.planPeriod === "monthly" || billingTerms.planPeriod === "yearly") ? billingTerms.monthlyStartDate : null,
        publish,
      };
      await createManualStore(input);
      onCreated();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "No se pudo guardar el negocio.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="border-sand/50 bg-paper mt-4 rounded-lg border p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-ink text-lg font-bold">Registrar negocio manual</h2>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cerrar
        </Button>
      </div>

      <div className="border-sand/40 mt-5 rounded-md border p-4">
        <h3 className="font-display text-ink text-base font-bold">Redes sociales</h3>
        <p className="text-mist mt-1 text-sm">Opcionales. Añade enlaces directos a perfiles públicos.</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          {(["tiktok", "instagram", "facebook"] as const).map((network) => {
            const label =
              network === "tiktok" ? "TikTok" : network === "instagram" ? "Instagram" : "Facebook";
            const placeholder =
              network === "tiktok"
                ? "https://www.tiktok.com/@tunegocio"
                : network === "instagram"
                  ? "https://www.instagram.com/tu-negocio/"
                  : "https://www.facebook.com/tu.negocio";
            return (
              <Input
                key={network}
                label={label}
                type="url"
                value={socialLinks[network]}
                placeholder={placeholder}
                onChange={(event) =>
                  setSocialLinks((current) => ({ ...current, [network]: event.target.value }))
                }
              />
            );
          })}
        </div>
      </div>

      <BillingFields mode="new-listing" value={billingTerms} onChange={setBillingTerms} />
      <p className="text-mist mt-1 text-sm">
        Se guarda en el directorio con origen manual. No aparece en la sincronizacion del SaaS.
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Input label="Nombre comercial" value={form.name} onChange={(e) => set("name", e.target.value)} />
        <CategorySelect
          label="Categoria"
          value={categoryId}
          onChange={setCategoryId}
          hint="Categoria controlada por Vimos. Elige la general o una subcategoria."
        />
        <UbigeoSelect
          idPrefix="manual-store-ubigeo"
          className="sm:col-span-2"
          value={{ department: form.department, province: form.province, district: form.district }}
          onChange={(next) => setForm((previous) => ({ ...previous, ...next }))}
        />
        <Input label="Direccion" value={form.address} onChange={(e) => set("address", e.target.value)} />
        <Input label="Telefono" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        <Input label="WhatsApp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />
        <Input
          label="Pagina web"
          value={form.website_url}
          onChange={(e) => set("website_url", e.target.value)}
          hint="Opcional. Si se deja vacio, la ficha no tendra enlace externo."
        />
        <KeywordsInput
          className="sm:col-span-2"
          label="Palabras clave de busqueda"
          value={keywords}
          onChange={setKeywords}
          hint="Opcional. Hasta 3 palabras que ayuden a encontrar el negocio en /tiendas."
        />
      </div>

      <div className="mt-4">
        <label className="font-body text-ink text-sm font-medium">Descripcion</label>
        <textarea
          className="border-sand/70 bg-paper text-ink focus-visible:ring-gold focus-visible:ring-offset-paper mt-1.5 min-h-24 w-full rounded-md border px-3.5 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          maxLength={2000}
        />
      </div>

      <div className="border-sand/40 mt-5 rounded-md border p-4">
        <h3 className="font-display text-ink text-base font-bold">Ubicacion en el mapa</h3>
        <p className="text-mist mt-1 text-sm">
          Opcional. Busca la direccion y confirma el punto exacto del local para que la ficha muestre su
          ubicacion en el mapa.
        </p>
        <LocationPicker
          className="mt-4"
          value={location}
          onChange={setLocation}
          addressHint={form.address || form.district || form.province}
        />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div>
          <label className="font-body text-ink text-sm font-medium">Logo</label>
          <input
            type="file"
            accept="image/*"
            className="mt-1.5 block text-sm"
            onChange={(e) => handleImage(e, "logo")}
          />
          {logoUrl ? <img src={logoUrl} alt="Logo" className="mt-2 h-16 w-16 rounded object-cover" /> : null}
        </div>
        <div>
          <label className="font-body text-ink text-sm font-medium">Portada</label>
          <input
            type="file"
            accept="image/*"
            className="mt-1.5 block text-sm"
            onChange={(e) => handleImage(e, "cover")}
          />
          {coverUrl ? (
            <img src={coverUrl} alt="Portada" className="mt-2 h-16 w-28 rounded object-cover" />
          ) : null}
        </div>
        <div>
          <label className="font-body text-ink text-sm font-medium">Galeria (max. 8)</label>
          <input
            type="file"
            accept="image/*"
            multiple
            className="mt-1.5 block text-sm"
            onChange={(e) => handleImage(e, "gallery")}
          />
          {gallery.length > 0 ? <p className="text-mist mt-2 text-xs">{gallery.length} imagen(es)</p> : null}
        </div>
      </div>

      <label className="text-ink mt-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={publish}
          onChange={(e) => setPublish(e.target.checked)}
        />
        Publicar de inmediato en el directorio
      </label>

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

      <div className="mt-5 flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" isLoading={isSaving}>
          Guardar negocio
        </Button>
      </div>
    </form>
  );
}
